import { useEffect, useMemo, useState } from "react";
import { Page, DataTable } from "@iq/models";
import { IoDownloadOutline } from "react-icons/io5";
import { fetchAppointmentReceipt, fetchAppointmentTransactions } from "../services/customerService";
import "../styles/order.css";
import useIsSmallScreen from "../hooks/useIsSmallScreen";

function Orders() {
    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const isSmallScreen = useIsSmallScreen();

    useEffect(() => {
        let active = true;
        async function loadTransactions() {
            try {
                const payload = await fetchAppointmentTransactions();
                if (active) setAppointments(payload?.appointments || []);
            } catch (error) {
                if (active) setErrorMessage(error.message || "Failed to load transactions");
            } finally {
                if (active) setIsLoading(false);
            }
        }
        loadTransactions();
        return () => { active = false; };
    }, []);

    async function downloadReceipt(appointmentId) {
        try {
            setErrorMessage("");
            const pdf = await fetchAppointmentReceipt(appointmentId);
            const url = window.URL.createObjectURL(pdf);
            const link = document.createElement("a");
            link.href = url;
            link.download = `appointment-receipt-${appointmentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (error) {
            setErrorMessage(error.message || "Failed to download receipt");
        }
    }

    const tableConfig = useMemo(() => ({
        columns: [
            { key: "id", label: "Appointment", accessor: "id", width: "0.8fr", formatter: (id) => `#${id}`, filterable: true },
            { key: "customer_name", label: "Customer", accessor: "customer_name", width: "1.4fr", filterable: true },
            { key: "service_name", label: "Service", accessor: "service_name", width: "1.4fr", filterable: true },
            { key: "start_at", label: "Date", accessor: "start_at", width: "1fr", formatter: formatDate, filterable: true },
            { key: "payment_status", label: "Payment", accessor: "payment_status", width: "1fr", filterable: true, quickValues: ["deposit_paid", "Completed"] },
            { key: "payment_method", label: "Method", accessor: "payment_method", width: "0.8fr", formatter: formatPaymentMethod, filterable: true, quickValues: ["Cash", "Card"] },
            {
                key: "receipt", label: "Receipt", accessor: "id", width: "1fr", searchable: false, filterable: false,
                renderCell: ({ row }) => (
                    <button className="transaction-receipt-button" type="button" onClick={(event) => { event.stopPropagation(); downloadReceipt(row.id); }}>
                        <IoDownloadOutline aria-hidden="true" /> Download PDF
                    </button>
                ),
            },
        ],
        emptyLabel: "No paid appointments found.",
        searchPlaceholder: "Search transactions",
    }), []);

    const mobileTableConfig = useMemo(() => ({
        ...tableConfig,
        columns: tableConfig.columns.filter((column) => ["id", "customer_name", "start_at", "payment_method", "receipt"].includes(column.key)),
    }), [tableConfig]);

    return (
        <Page>
            {errorMessage && <p className="error" role="alert">{errorMessage}</p>}
            {isLoading ? <p>Loading transactions...</p> : (
                <DataTable
                    rowData={appointments}
                    rowKey={(appointment) => appointment.id}
                    tableConfig={isSmallScreen ? mobileTableConfig : tableConfig}
                />
            )}
        </Page>
    );
}

function formatDate(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
}

function formatPaymentMethod(value) {
    return value || "-";
}

export default Orders;